import React, { useEffect, useRef } from "react";
import * as historyStore from "./historyStore";

/**
 * The active find-similar chain, as clickable reference-image thumbnails:
 * "this search came from that one." Rendered between the toolbar and the
 * filter bar, only once there is an actual chain (≥2 nodes) — a single
 * search needs no provenance.
 *
 * Each node is a plain link to its entry's URL, so clicks ride the same
 * URL-driven navigation path as Back/Forward — no bespoke replay wiring
 * here. onNavigate intercepts to keep it a router push (a raw href would
 * full-page reload).
 */
export default function TrailStrip({ currentEntryId, onNavigate }) {
    // A long chain scrolls inside the strip; keep the node the user is ON
    // visible without them having to hunt for it.
    const currentRef = useRef(null);
    useEffect(() => {
        if (currentRef.current && currentRef.current.scrollIntoView) {
            currentRef.current.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
    }, [currentEntryId]);

    if (!currentEntryId) return null;

    // The trail is a TIMELINE of tabs, not a provenance path: every search
    // in the session stays visible, in the order the searches were run, and
    // a new search always joins at the END — even when it branched off an
    // earlier tab. Stepping back never hides later tabs, and deviating never
    // replaces them; the arrows read as sequence, not strict "came from".
    //
    // Session = the current entry's root (walk parentId up; stop at the
    // first evicted parent — the 20-cap prunes oldest-first) plus everything
    // that descends from that root across ALL branches, ordered by
    // createdAt so tabs never reorder when an entry is merely re-searched
    // (updatedAt moves, createdAt doesn't). Visited sets make a corrupted
    // store's cycle terminate instead of hanging.
    const entries = historyStore.list();
    const byId = new Map(entries.map((e) => [e.id, e]));

    let root = byId.get(currentEntryId);
    const seen = new Set();
    while (root && root.parentId && byId.get(root.parentId) && !seen.has(root.id)) {
        seen.add(root.id);
        root = byId.get(root.parentId);
    }
    if (!root) return null;

    const chain = [];
    const visited = new Set();
    const queue = [root];
    while (queue.length) {
        const n = queue.shift();
        if (!n || visited.has(n.id)) continue;
        visited.add(n.id);
        chain.push(n);
        entries.forEach((e) => {
            if (e.parentId === n.id) queue.push(e);
        });
    }
    chain.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

    if (chain.length < 2) return null;

    return (
        <div className="il-trail-strip" aria-label="Search trail">
            <span className="il-trail-label">Trail:</span>
            {chain.map((entry, i) => {
                const isCurrent = entry.id === currentEntryId;
                const href = historyStore.entryHref(entry);
                const label = entry.q
                    ? `Text search — “${entry.q}” — ${(entry.results || []).length} results`
                    : entry.src
                        ? `Search ${i + 1} of ${chain.length} — ${(entry.results || []).length} results`
                        : `Uploaded image search — ${(entry.results || []).length} results`;
                return (
                    <React.Fragment key={entry.id}>
                        {i > 0 && <span className="il-trail-arrow" aria-hidden="true">→</span>}
                        <a
                            ref={isCurrent ? currentRef : undefined}
                            className={"il-trail-node" + (isCurrent ? " il-trail-current" : "")}
                            href={href}
                            title={label}
                            aria-label={label}
                            aria-current={isCurrent ? "page" : undefined}
                            onClick={(e) => {
                                e.preventDefault();
                                if (!isCurrent) onNavigate(href);
                            }}
                        >
                            {entry.q ? (
                                /* Text searches have no image — the query IS
                                   the identity, shown as a quoted chip. */
                                <span className="il-trail-node-text">“{entry.q.length > 24 ? entry.q.slice(0, 24) + '…' : entry.q}”</span>
                            ) : (
                                <img src={entry.thumb || entry.src} alt="" loading="lazy" />
                            )}
                        </a>
                    </React.Fragment>
                );
            })}
        </div>
    );
}
